import React, { useState } from "react";
import { Container, Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import Meta from "../../components/common/Meta";
import { DrawerHeader } from "../../components/layout/Drawer/DrawerMain";
import { uiHeight } from "../../utils/uiHeight";
import { ResearchCreateRequest } from "./types";
import { deepResearchApi } from "./api";
import { GradientTypography } from "../../utils/gradientTypography";
import { useAlert } from "../../context/AlertContext";
import { useCustomNavigate } from "../../hooks/useCustomNavigate";
import ProPlanModal from "../../components/common/ProPlanModal";

// コンポーネント
import SearchAndFilters from "./components/SearchAndFilters";
import ResearchList from "./components/ResearchList";
import CreateResearchDialog from "./components/CreateResearchDialog";
import DeleteConfirmDialog from "./components/DeleteConfirmDialog";
import DownloadMenu from "./components/DownloadMenu";

// カスタムフック
import { useResearchList } from "./hooks/useResearchList";

const DeepResearch: React.FC = () => {
  const navigate = useCustomNavigate();
  const { setAlert } = useAlert();
  const { t } = useTranslation();

  // カスタムフックから調査一覧の状態と関数を取得
  const {
    researches,
    loading,
    page,
    setPage,
    totalPages,
    statusFilter,
    setStatusFilter,
    searchQuery,
    setSearchQuery,
    loadResearches,
  } = useResearchList();

  // ダイアログとメニューの状態
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetUuid, setDeleteTargetUuid] = useState<string>("");
  const [deleting, setDeleting] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedResearchUuid, setSelectedResearchUuid] = useState<string>("");

  // クレジット不足ダイアログの状態
  const [openProDialog, setOpenProDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState<string | undefined>(undefined);

  const handleCreateResearch = async (createForm: Omit<ResearchCreateRequest, "ai_mode">) => {
    setCreating(true);

    try {
      const response = await deepResearchApi.startResearch(createForm);

      if (response.data.success) {
        const researchUuid = response.data.research_uuid;
        setCreateDialogOpen(false);
        navigate(`/deep-research/${researchUuid}`);
        loadResearches();
      } else {
        throw new Error(response.data.error || t("deepResearch.errors.startFailed"));
      }
    } catch (error: unknown) {
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
        if (axiosError.response?.status === 402) {
          const errorMessage = axiosError.response?.data?.message;
          setCreateDialogOpen(false);

          if (errorMessage === "NO_CREDITS" || errorMessage === "クレジットが不足しています。") {
            setDialogMessage(t("deepResearch.errors.noCredit"));
            setOpenProDialog(true);
          } else {
            setAlert("error", t("deepResearch.errors.startFailed"));
          }
        } else {
          setAlert("error", t("deepResearch.errors.startFailed"));
        }
      } else {
        setAlert("error", t("deepResearch.errors.startFailed"));
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteDialogOpen = (uuid: string) => {
    setDeleteTargetUuid(uuid);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    if (!deleting) {
      setDeleteDialogOpen(false);
      setDeleteTargetUuid("");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetUuid) return;

    setDeleting(true);
    try {
      await deepResearchApi.deleteResearch(deleteTargetUuid);
      setAlert("success", t("deepResearch.success.deleted"));
      loadResearches();
      setDeleteDialogOpen(false);
      setDeleteTargetUuid("");
    } catch {
      setAlert("error", t("deepResearch.errors.deleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>, uuid: string) => {
    event.stopPropagation();
    setDownloadMenuAnchor(event.currentTarget);
    setSelectedResearchUuid(uuid);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
    setSelectedResearchUuid("");
  };

  const handleExportResearch = async (format: "markdown" | "word" | "txt") => {
    try {
      const response = await deepResearchApi.exportResearch(selectedResearchUuid, { format });

      const blob = new Blob([response.data.content], { type: response.data.content_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setAlert("success", t("deepResearch.success.downloaded", { format }));
    } catch {
      setAlert("error", t("deepResearch.errors.downloadFailed"));
    }
  };

  const handleSaveToTextFile = async (uuid: string) => {
    try {
      const response = await deepResearchApi.getResearchDetail(uuid);
      const research = response.data;

      if (research && research.final_report) {
        const title = research.title || research.query;
        navigate("/notes-nebula/files/new", {
          state: {
            content: research.final_report,
            title: title.length > 36 ? title.slice(0, 36) : title,
          },
        });
      } else {
        setAlert("error", t("deepResearch.errors.reportNotFound"));
      }
    } catch {
      setAlert("error", t("deepResearch.errors.reportFetchFailed"));
    }
  };

  const handleViewResearch = (uuid: string) => {
    navigate(`/deep-research/${uuid}`);
  };

  return (
    <Box>
      <Meta title="Deep Research" meta={[{ name: "robots", content: "noindex, nofollow" }]} />
      <DrawerHeader />

      <Container
        maxWidth="xl"
        sx={{
          pb: 2,
          display: "flex",
          flexDirection: "column",
          height: uiHeight(),
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", flexDirection: "row", fontWeight: "bold", zIndex: 100, alignItems: "flex-end" }}>
            <GradientTypography
              variant="h4"
              sx={{ fontWeight: 600, cursor: "pointer", mb: 0.5 }}
              component="h1"
              onClick={() => navigate("/deep-research")}
            >
              {t("deepResearch.title")}
            </GradientTypography>
            <Typography variant="subtitle1" component="span" sx={{ ml: 1, mb: 0.5 }} color={"#fff"} gutterBottom>
              Beta
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t("deepResearch.description")}
          </Typography>
        </Box>

        {/* 検索・フィルター */}
        <SearchAndFilters
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          onCreateNew={() => setCreateDialogOpen(true)}
          creating={creating}
        />

        {/* 調査一覧 */}
        <ResearchList
          researches={researches}
          loading={loading}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          onViewResearch={handleViewResearch}
          onDeleteResearch={handleDeleteDialogOpen}
          onDownloadResearch={handleDownloadMenuOpen}
          onSaveToTextFile={handleSaveToTextFile}
          onCreateNew={() => setCreateDialogOpen(true)}
        />

        {/* ダウンロードメニュー */}
        <DownloadMenu
          anchorEl={downloadMenuAnchor}
          open={Boolean(downloadMenuAnchor)}
          onClose={handleDownloadMenuClose}
          onExport={handleExportResearch}
        />

        {/* 新規調査作成ダイアログ */}
        <CreateResearchDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSubmit={handleCreateResearch}
          creating={creating}
        />

        {/* 削除確認ダイアログ */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onClose={handleDeleteDialogClose}
          onConfirm={handleDeleteConfirm}
          deleting={deleting}
        />

        {/* クレジット不足ダイアログ */}
        <ProPlanModal open={openProDialog} setOpen={setOpenProDialog} message={dialogMessage} />
      </Container>
    </Box>
  );
};

export default DeepResearch;
